
import { Box } from "@mui/material";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useAutoRefresh } from "../../hooks/use-auto-refresh/UseAutoRefresh";
import { UIRefreshCategoryEnum } from "../../common/enums/UIRefreshCategoryEnum";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import LoadingZone from "../loading-zone/LoadingZone";
import { forwardRef, Ref, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { BaseActionEnum } from "../action-menu/ActionMenuItem";
import { DeviceTypesEnum } from "../../common/enums/DeviceTypesEnum";
import { isMobile, isTablet } from 'react-device-detect';
import { useObjectGraphData } from "../../queries/object-graph/UseObjectGraphData";
import { GraphOptions, ITrendingDisplayOptions } from "../../services/trending-data-service/Models";
import { LiquidProductionTATEnum } from "../../common/enums/LiquidProductionTATEnum";
import { StandaloneTankTATEnum } from "../../common/enums/StandaloneTankTATEnum";
import Graph, { GraphType } from "../graph/Graph";
import ObjectGraphUtils from "../object-graph/ObjectGraphUtils";
import { DataTooltipDisplayEnum } from "../../services/user-settings-data/Models";
import { useTranslation } from "react-i18next";
import NoData from "../no-data/NoData";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { IObjectViewState } from "../../services/state-data-service/Models";
import { IDateRange } from "../../common/models/IDateRange";
import { TrendConfigurationModel } from "../../services/trending-configuration-data-service/Models";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { SeriesLineOptions, SeriesOptionsType } from "highcharts";
import { useTagValues } from "../../queries/tag-values/UseTagValues";
import Enumerable from "linq";
import { useEvaluateFeature } from "../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../services/features-data-service/Models";
import { useCustomerAttribute } from "../../queries/attributes/UseCustomerAttribute";
import { IEntityValueDeviceModel, IEntityValueTankModel } from "../../common/models/IEntityValueModels";

export type TankLevelGraphProps = {
    tank: IEntityValueTankModel | IEntityValueDeviceModel,
    dateRange?: IDateRange,
    displayOptions?: ITrendingDisplayOptions
};

export type TankLevelGraphType = {
    executeAction: (action: BaseActionEnum) => void;
    reflow: () => void;
};

function TankLevelGraph({
     //visible
     tank,
     dateRange,
     displayOptions
}: TankLevelGraphProps, ref: Ref<unknown> | undefined) {

    const { t } = useTranslation();

    const [graphOptions, setGraphOptions] = useState<GraphOptions>();

    const routeContext = useRouteContext();
    const [userState] = useUserContext();    
    const [offlineState] = useOfflineDetectContext();

    const graphRef = useRef<GraphType>(undefined);

    const isPhone = isMobile && !isTablet;    

    useImperativeHandle<unknown, TankLevelGraphType>(
        ref,
        () => ({
            executeAction: executeAction,
            reflow: reflow
        })
    );

    const { data: objectViewState } = useObjectViewState({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: {
            id: routeContext.contextObject?.id as number,
            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum
        },
        offline: offlineState.offline
    });

    const standAloneTank = routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Device && 
                            objectViewState && 
                            (objectViewState as IObjectViewState).deviceType === DeviceTypesEnum.StandaloneTank;


    const {data: defaultGraphScale} = useCustomerAttribute({
        groupID: routeContext.groupID!,
        name: 'DefaultDetailsGraphScale',
        enabled: standAloneTank
    });

    const autoScaleY = standAloneTank && defaultGraphScale?.attributeValue?.toLowerCase() === 'auto';

    const tankTrendConfig = useMemo(() => {
        const eLynxObject = routeContext.contextObject as IeLynxObject;

        return TrendConfigurationModel.getTankDetailsTrendingConfig(eLynxObject, [{
                id: eLynxObject.id,
                objectType: eLynxObject.objectType,
                excludeObject: false,
                includeSubgroups: false,
                includeDynamicGroups: true,
                targetObjectType: eLynxObject.objectType,
                includeWellDevices: false
            }
        ], standAloneTank ? [
                StandaloneTankTATEnum.LiquidHeight
            ] : [
                LiquidProductionTATEnum.TopLevel,
                LiquidProductionTATEnum.InterfaceLevel
        ],
        t('objectTagValues.tankLevels'),
        );

    }, [
        t,
        routeContext.contextObject,
        standAloneTank
    ]);

    const {
        data: trendData,
        //isFetching: isFetchingGraphData,
        status: trendStatus,
        refetch: refetchTrendData
    } = useQueryNotifications(useObjectGraphData)({
        contextGroupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject,
        configurationID: undefined,//state.configurationID,
        configuration: tankTrendConfig,
        duration: {
            startDate: dateRange?.startDate,
            endDate: dateRange?.endDate
        },
        displayOptions: displayOptions,
    });


    const { data: tankModel } = useQueryNotifications(useTagValues)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        objectID: routeContext.contextObject?.id as number,
        objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
        date: undefined,
        offline: offlineState.offline,
        manualEntry: false,
        useLKVOffline: false,
    });

    const liquidHeightTag = standAloneTank ? 
                            tankModel?.tags?.find(o => o.tatID === StandaloneTankTATEnum.LiquidHeight) : 
                            undefined;

    // AutoRefresh support
    useAutoRefresh({
        dataRefresh: () => {
            refetchTrendData();
        }, 
        category: UIRefreshCategoryEnum.Details
    });

    const reflow = () => {
        graphRef.current?.reflow();
    }

    function executeAction(action: BaseActionEnum) {

        switch (action) {
            case BaseActionEnum.Refresh:
                refetchTrendData();
                break;
        }
    }

    const noDataMessage = trendStatus === 'success' && 
                            ((trendData?.trendViewModels?.length ?? 0) === 0 || 
                            !trendData?.trendViewModels?.find(o => (o.chartOptions?.series?.length ?? 0) > 0)) ? 
                            t('objectTagValues.noTankLevelData') : 
                            undefined;

    useEffect(() => {
        

            if (trendData && trendData.trendViewModels && (trendData.trendViewModels?.length ?? 0) > 0) {

                const options = ObjectGraphUtils.getGraphOptions(routeContext.contextObject, 
                                                        trendData, 
                                                        DataTooltipDisplayEnum.Separate,
                                                        undefined,
                                                        undefined,
                                                        0);


            // chart options returned do not match the types, so cast to any in order to set step
            options?.optionItems?.flatMap(o => o.series).forEach(v => {
                (v as any).step = false;
                (v as any).lineWidth = 2;
            });

            // We always want to display the liquid height in feet and inches for standalone tanks.
            // User Story 51753: Tank Details View - Graph - Use Feet instead of Inches
            // Bug 51934: Tank Level Graph yAxis
            // Bug 52688: Liquid Production Tank Graph Incorrect
            if (liquidHeightTag) {
                convertToFeetAndInches(options, liquidHeightTag.unitDisplayName);
            }

            if (standAloneTank /*&& autoScaleY*/) {
                options?.optionItems.forEach(o => {

                    if (o.series && o.series.length) {

                        const minY = autoScaleY
                            ? Enumerable.from(o.series!)
                                .selectMany(o => (o as any).data)
                                .select(o => (o as any)[1])
                                .min()
                            : tank.tankStyle.elevatedTank
                                ? (tank.tankStyle.elevationInInches ?? 0) / 12
                                : 0
                        
                        const maxY = tank.tankStyle.elevatedTank
                            ? ((tank.tankStyle.elevationInInches ?? 0) + (tank.tankStyle.heightInInches ?? 0)) / 12
                            : undefined;

                        if (o.yAxis !== undefined) {
                            const yAxis = o.yAxis as Highcharts.YAxisOptions[];
                            if (yAxis && yAxis.length > 0 && yAxis[0]) {
                                yAxis[0].min = minY;
                                yAxis[0].max = maxY;
                            }
                        }                        
                    }
                });
            }

            setGraphOptions(options); 
        }
        }, [
            trendData,
            setGraphOptions,
            routeContext.contextObject,
            liquidHeightTag,
            standAloneTank,
            autoScaleY,
            tank.tankStyle.elevatedTank,
            tank.tankStyle.elevationInInches,
            tank.tankStyle.heightInInches
        ]);
    
    return (
           
            <LoadingZone isLoading={trendStatus === 'loading'}>                                
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: '1 1 auto',
                    overflow: "hidden",
                    //overflowY: isPhone ? 'auto' : undefined,
                    padding: 1
                }}>
                        
                        {!offlineState.offline && 


                                <Box  id='mygraph' sx={{
                                    padding: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: `1 1 ${isPhone ? '100%' : '50%'}`,
                                    '& + &': {
                                        marginTop: 1
                                    },
                                    //minHeight: isPhone ? 300 : undefined
                                    minHeight: isPhone ? 300 : 200
                                }}>
          
                                    {!noDataMessage && graphOptions && 
                                        <Graph ref={graphRef}
                                            options={graphOptions} 
                                            showAxesToggle={false} />
                                    }

                                    {noDataMessage &&
                                        <NoData text={noDataMessage} />
                                    }

                                 </Box>                                    
                        }

                </Box>
            </LoadingZone>                
            

        
    );
}

function convertToFeetAndInches(options: GraphOptions | undefined, tagUnits: string | undefined) {
    options?.optionItems.forEach(optionItem => {
        optionItem.series?.forEach(series => {
            const type = series.type;

            if (type === 'line') {
                const lineSeries = series as SeriesLineOptions;

                (lineSeries as any).tooltip = {
                    ...lineSeries.tooltip,
                    pointFormatter: function () {
                        const point = this as any;
                        return formatTooltipPoint(point);
                    }
                };

                // If the tag units are in inches, convert to feet, otherwise
                // leave the data as is. In either case, the tooltip will display
                // the data in feet and inches.
                if(tagUnits?.toLowerCase() === "inches" )
                {
                    lineSeries.data?.forEach(datum => {
                        const point = datum as [number, number];
                        point[1] = Math.round(point[1] / 12 * 100) / 100;
                    });
                }
            }
        });
    });
}

function formatTooltipPoint(point: any) {
    const feet = Math.floor(point.y);
    const inches = Math.round((point.y - feet) * 1200) / 100;

    return `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${feet}' ${inches}"</b><br/>`;
}

export default forwardRef(TankLevelGraph);